import React from 'react';
import { useNestedNavigation } from '../../../../../../../utils/appNavigationNames';
import useCareTeamMember from '../../../../../../../hooks/api/useCareTeamMember';
import { getCareTeamMemberDescriptorDisplay } from '../../../../../../../utils/careTeamUtils';
import BaseReference from './BaseReference';
import { ResolvedChatReferencedMedicalProfileFileArtifact } from '../../../../../../../primary-record-types/LambdaInvocationTypes';
import useMedicalProfileFiles from '../../../../../../../hooks/api/useMedicalProfileFiles';
import { getIntl } from '../../../../../../../utils/intl';
import useNotes from '../../../../../../../hooks/api/useNotes';
import { APITypes, MedicalDataTypes } from '../../../../../../../primary-record-types';
import useMedicalProfileResource from '../../../../../../../hooks/api/useMedicalProfileResource';
import Stack from '../../../../../../v2BaseComponents/Stack';
import { useResourceTitle } from '../../../../../../../utils/resourceUtils/getResourceTitle';

const intl = getIntl('chat-references', {
    page: '{fileName} - Page {page}',
    pageWithTotal: '{fileName} - Page {page} of {totalPages}',
    audioTime: '{fileName} - from {startTime} to {endTime}',
});

const msToTime = (duration: number) => {
    const seconds = Math.floor((duration / 1000) % 60);
    const minutes = Math.floor((duration / (1000 * 60)) % 60);
    const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

    const hoursString = hours < 10 ? '0' + hours : hours;
    const minutesString = minutes < 10 ? '0' + minutes : minutes;
    const secondsString = seconds < 10 ? '0' + seconds : seconds;

    return hours > 0 ? `${hoursString}:${minutesString}:${secondsString}` : `${minutesString}:${secondsString}`;
};

const CareTeamMemberReference: React.FC<{ medicalProfileId: string; careTeamMemberId: string }> = ({
    medicalProfileId,
    careTeamMemberId,
}) => {
    const { nestedProfileNavigation } = useNestedNavigation();
    const { isLoadingCareTeamMember, careTeamMember } = useCareTeamMember({
        medicalProfileId,
        careTeamMemberId,
    });

    const getName = () => {
        if (!careTeamMember) return null;

        return getCareTeamMemberDescriptorDisplay(careTeamMember);
    };

    return (
        <BaseReference
            text={getName()}
            isLoading={isLoadingCareTeamMember}
            onPress={() =>
                nestedProfileNavigation('care-team-member', { profileId: medicalProfileId, careTeamMemberId })
            }
        />
    );
};

const FileArtifactReference: React.FC<{
    medicalProfileId: string;
    artifact: ResolvedChatReferencedMedicalProfileFileArtifact;
}> = ({ artifact }) => {
    const { nestedProfileNavigation } = useNestedNavigation();
    const { file, isLoadingFile } = useMedicalProfileFiles({
        medicalProfileFileId: artifact.medicalProfileFileId,
        medicalProfileId: artifact.medicalProfileId,
    });

    const getName = () => {
        if (isLoadingFile || !file) return null;

        if (artifact.type === 'page' && file.pageCount)
            return intl('pageWithTotal', {
                page: artifact.page,
                totalPages: file.pageCount,
                fileName: file.fileName,
            });
        else if (artifact.type === 'page') return intl('page', { page: artifact.page, fileName: file.fileName });
        else
            return intl('audioTime', {
                startTime: msToTime(artifact.startTime),
                endTime: msToTime(artifact.endTime),
                fileName: file.fileName,
            });
    };

    return (
        <BaseReference
            text={getName()}
            isLoading={isLoadingFile}
            onPress={() =>
                nestedProfileNavigation('file', {
                    fileId: artifact.medicalProfileFileId,
                    startTime: artifact.type === 'audio' ? artifact.startTime.toString() : undefined,
                    page: artifact.page.toString(),
                    profileId: artifact.medicalProfileId,
                })
            }
        />
    );
};

const NoteReference: React.FC<{
    noteId: string;
}> = ({ noteId }) => {
    const { nestedProfileNavigation } = useNestedNavigation();
    const { note, isLoadingNote } = useNotes({
        noteId,
    });

    const getName = () => {
        if (isLoadingNote || !note) return null;

        return note.title;
    };

    return (
        <BaseReference
            text={getName()}
            isLoading={isLoadingNote}
            onPress={() => {
                if (note && note.medicalProfileId) {
                    nestedProfileNavigation('note', {
                        noteId: note.noteId,
                        noteEntityType: note.entityType,
                        profileId: note.medicalProfileId,
                    });
                }
            }}
        />
    );
};

const ResourceReference: React.FC<{
    medicalProfileId: string;
    resourceId: string;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
}> = ({ medicalProfileId, resourceCategory, resourceId }) => {
    const { nestedProfileNavigation } = useNestedNavigation();
    const { isLoading, resource } = useMedicalProfileResource({
        medicalProfileId: medicalProfileId,
        resourceCategory: resourceCategory,
        resourceId: resourceId,
    });

    const resourceTitle = useResourceTitle(resource, medicalProfileId);

    return (
        <BaseReference
            text={resourceTitle}
            isLoading={isLoading}
            onPress={() => {
                if (resource.type !== 'lab')
                    nestedProfileNavigation('data-details', {
                        resourceId: resourceId,
                        category: resource.type,
                        profileId: medicalProfileId,
                    });
                else
                    nestedProfileNavigation('lab-history', {
                        profileId: medicalProfileId,
                        labCode: resource.labType.code,
                    });
            }}
        />
    );
};

export const ReferencesDisplay: React.FC<{ referencedResources: APITypes.chatService.ModelReferencedResource[] }> = ({
    referencedResources,
}) => {
    return (
        <Stack vertical align="flex-start" fullWidth>
            {referencedResources.map((resource, index) => {
                if (resource.category === 'care-team') {
                    return (
                        <CareTeamMemberReference
                            key={index}
                            medicalProfileId={resource.medicalProfileId}
                            careTeamMemberId={resource.id}
                        />
                    );
                } else if (resource.category === 'file-artifact') {
                    return (
                        <FileArtifactReference
                            key={index}
                            medicalProfileId={resource.medicalProfileId}
                            artifact={resource}
                        />
                    );
                } else if (resource.category === 'note') {
                    return <NoteReference key={index} noteId={resource.id} />;
                } else {
                    return (
                        <ResourceReference
                            key={index}
                            medicalProfileId={resource.medicalProfileId}
                            resourceId={resource.id}
                            resourceCategory={resource.category}
                        />
                    );
                }
            })}
        </Stack>
    );
};
